import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type FormEvent,
  type ReactNode,
} from "react";
import { canPerform, type EffectiveContentResponse } from "@vital/shared";
import { useAuth } from "../context/AuthContext";
import { PageHeader } from "../components/PageHeader";
import { Card } from "../components/Card";
import { MetricCard } from "../components/MetricCard";
import { EffectiveContentPanel } from "../components/EffectiveContentPanel";
import { OpsEmptyState } from "../components/ops-display";
import { InspectPanel } from "../components/InspectPanel";
import {
  SiteGroupsRoster,
  SiteInspectSummary,
  SitesFilterBar,
  SitesInventoryList,
} from "../components/sites-inventory";
import {
  ComposerHeader,
  ComposerPanel,
  ComposerSection,
  ComposerWorkspace,
} from "../components/ComposerChrome";
import { cmsStrings } from "../terminology";
import { api, ApiError, type ResellerProfileSummary } from "../lib/api";
import { useInspectSearchParam } from "../lib/use-inspect-search-param";
import {
  filterAndSortSites,
  type SiteInventoryRow,
  type SiteSortKey,
  type SiteStatusFilter,
} from "../lib/sites-ops";

const GROUP_NAME_MAX = 120;
const GROUP_DESC_MAX = 500;

type Site = { id: string; code: string; name: string; location: string | null };
type AdminSite = Site & { active: boolean; codeLocked: boolean };
type SiteGroup = {
  id: string;
  name: string;
  description: string;
  active: boolean;
  members: Array<{ siteId: string; siteCode: string; siteName: string }>;
};
type SiteMembership = { groupId: string; groupName: string };
type OverlapRow = {
  siteId: string;
  siteCode: string;
  organizationId: string;
  groups: Array<{ id: string; name: string; active: boolean }>;
};

export function SitesPage() {
  const { user } = useAuth();
  const authInput = {
    platformRole: user?.platformRole ?? null,
    membershipRole: user?.membershipRole ?? null,
    legacyRole: user?.role,
  };
  const canManageSites = canPerform({ ...authInput, permission: "sites.manage" });
  const canManageGroups = canPerform({ ...authInput, permission: "groups.manage" });
  const canPreviewEffective = canPerform({ ...authInput, permission: "content.create" });
  const canResolveOverlaps = canPerform({ ...authInput, permission: "org.entitlements.manage" });
  const canReadResellers = canPerform({ ...authInput, permission: "reseller.profile.read" });

  const ui = cmsStrings.sitesUi;
  const c = cmsStrings.sitesComposer;

  const [sites, setSites] = useState<Site[]>([]);
  const [allSites, setAllSites] = useState<AdminSite[]>([]);
  const [groups, setGroups] = useState<SiteGroup[]>([]);
  const [resellerProfiles, setResellerProfiles] = useState<ResellerProfileSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const [editingId, setEditingId] = useState<string | null>(null);
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [memberIds, setMemberIds] = useState<string[]>([]);
  const [confirmMoves, setConfirmMoves] = useState(false);
  const [saving, setSaving] = useState(false);

  const [siteEditingId, setSiteEditingId] = useState<string | null>(null);
  const [siteCode, setSiteCode] = useState("");
  const [siteName, setSiteName] = useState("");
  const [siteLocation, setSiteLocation] = useState("");
  const [siteSaving, setSiteSaving] = useState(false);

  const [previewSiteId, setPreviewSiteId] = useState("");
  const [effective, setEffective] = useState<EffectiveContentResponse | null>(null);
  const [effectiveLoading, setEffectiveLoading] = useState(false);
  const previewRef = useRef<HTMLDivElement | null>(null);

  const [selectedSiteId, setSelectedSiteId] = useInspectSearchParam();
  const [siteQuery, setSiteQuery] = useState("");
  const [siteStatusFilter, setSiteStatusFilter] = useState<SiteStatusFilter>("all");
  const [siteSort, setSiteSort] = useState<SiteSortKey>("name");

  const [overlaps, setOverlaps] = useState<OverlapRow[]>([]);
  const [overlapKeep, setOverlapKeep] = useState<Record<string, string>>({});
  const [resolvingOverlaps, setResolvingOverlaps] = useState(false);

  function localizeApiError(message: string): string {
    if (message.includes("already belongs to group") || message.includes("at most one operational")) {
      return c.groupMemberConflict;
    }
    if (message.includes("Not found")) return c.notFound;
    if (message.includes("Organization context required")) return c.orgRequired;
    return message;
  }

  const membershipBySite = useMemo(() => {
    const map = new Map<string, SiteMembership>();
    for (const group of groups) {
      for (const member of group.members) {
        map.set(member.siteId, { groupId: group.id, groupName: group.name });
      }
    }
    return map;
  }, [groups]);

  const pendingMoves = useMemo(() => {
    return memberIds
      .map((siteId) => {
        const membership = membershipBySite.get(siteId);
        if (!membership) return null;
        if (editingId && membership.groupId === editingId) return null;
        const site = sites.find((s) => s.id === siteId);
        return {
          siteId,
          siteCode: site?.code ?? siteId,
          siteName: site?.name ?? siteId,
          fromGroupId: membership.groupId,
          fromGroupName: membership.groupName,
        };
      })
      .filter((m): m is NonNullable<typeof m> => m != null);
  }, [memberIds, membershipBySite, editingId, sites]);

  const resetForm = () => {
    setEditingId(null);
    setName("");
    setDescription("");
    setMemberIds([]);
    setConfirmMoves(false);
  };

  const loadData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [siteList, groupList] = await Promise.all([api.listSites(), api.listSiteGroups()]);
      setSites(siteList);
      setGroups(groupList);
      if (canManageSites) {
        setAllSites(await api.listAllSites());
      }
      if (canReadResellers) {
        const resellers = await api.listResellerProfiles();
        setResellerProfiles(resellers.items);
      }
      if (canResolveOverlaps) {
        try {
          const overlapRes = await api.listSiteGroupOverlaps();
          setOverlaps(overlapRes.overlaps);
          const keep: Record<string, string> = {};
          for (const row of overlapRes.overlaps) {
            keep[row.siteId] = row.groups[0]?.id ?? "";
          }
          setOverlapKeep(keep);
        } catch {
          /* overlaps endpoint is platform-owner only; ignore soft failures */
        }
      }
      if (siteList.length > 0) {
        setPreviewSiteId((prev) =>
          prev && siteList.some((s) => s.id === prev) ? prev : siteList[0]!.id,
        );
      }
    } catch (err) {
      setError(err instanceof ApiError ? localizeApiError(err.message) : c.loadFailed);
    } finally {
      setLoading(false);
    }
  }, [canManageSites, canReadResellers, canResolveOverlaps]);

  const resellerBySiteId = useMemo(() => {
    const map = new Map<string, ResellerProfileSummary>();
    for (const profile of resellerProfiles) {
      for (const siteId of profile.linkedSiteIds) {
        map.set(siteId, profile);
      }
    }
    return map;
  }, [resellerProfiles]);

  const inventoryRows = useMemo((): SiteInventoryRow[] => {
    const source: Array<Site | AdminSite> = canManageSites ? allSites : sites;
    return source.map((site) => {
      const admin = site as AdminSite;
      const profile = canReadResellers ? resellerBySiteId.get(site.id) : undefined;
      let reseller: string | null = null;
      if (profile) {
        const stateNote =
          profile.state !== "published"
            ? ` (${profile.state === "archived" ? c.resellerArchivedSuffix : c.resellerDraftSuffix})`
            : "";
        reseller = `${profile.operationalName}${stateNote}`;
      }
      return {
        id: site.id,
        code: site.code,
        name: site.name,
        location: site.location,
        active: "active" in site ? admin.active : undefined,
        codeLocked: "codeLocked" in site ? admin.codeLocked : undefined,
        groupName: membershipBySite.get(site.id)?.groupName ?? null,
        resellerLabel: canReadResellers ? reseller : null,
      };
    });
  }, [allSites, sites, canManageSites, canReadResellers, membershipBySite, resellerBySiteId, c.resellerArchivedSuffix, c.resellerDraftSuffix]);

  const visibleSites = useMemo(
    () =>
      filterAndSortSites(inventoryRows, {
        query: siteQuery,
        status: siteStatusFilter,
        sort: siteSort,
      }),
    [inventoryRows, siteQuery, siteStatusFilter, siteSort],
  );

  const selectedSite = useMemo(
    () => inventoryRows.find((r) => r.id === selectedSiteId) ?? null,
    [inventoryRows, selectedSiteId],
  );

  const activeCount = useMemo(
    () => inventoryRows.filter((r) => r.active !== false).length,
    [inventoryRows],
  );
  const inactiveCount = useMemo(
    () => inventoryRows.filter((r) => r.active === false).length,
    [inventoryRows],
  );
  const ungroupedCount = useMemo(
    () => inventoryRows.filter((r) => !r.groupName?.trim()).length,
    [inventoryRows],
  );

  const groupRows = useMemo(
    () =>
      groups.map((group) => ({
        id: group.id,
        name: group.name,
        description: group.description,
        active: group.active,
        memberCount: group.members.length,
        memberCodes: group.members.map((m) => m.siteCode),
      })),
    [groups],
  );

  useEffect(() => {
    void loadData();
  }, [loadData]);

  useEffect(() => {
    if (loading) return;
    if (selectedSiteId && !inventoryRows.some((r) => r.id === selectedSiteId)) {
      setSelectedSiteId(null);
    }
  }, [inventoryRows, loading, selectedSiteId, setSelectedSiteId]);

  useEffect(() => {
    if (!selectedSiteId) return;
    function onKeyDown(e: KeyboardEvent) {
      if (e.key !== "Escape") return;
      if (resolvingOverlaps) return;
      e.preventDefault();
      setSelectedSiteId(null);
    }
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [selectedSiteId, resolvingOverlaps]);

  useEffect(() => {
    if (!editingId) return;
    document.getElementById("site-group-composer")?.scrollIntoView({ behavior: "smooth", block: "start" });
  }, [editingId]);

  useEffect(() => {
    if (!siteEditingId) return;
    document.getElementById("site-create-composer")?.scrollIntoView({ behavior: "smooth", block: "start" });
  }, [siteEditingId]);

  useEffect(() => {
    if (!canPreviewEffective || !previewSiteId) {
      setEffective(null);
      return;
    }
    let cancelled = false;
    setEffectiveLoading(true);
    void api
      .getEffectiveContent({ siteId: previewSiteId })
      .then((data) => {
        if (!cancelled) setEffective(data);
      })
      .catch((err) => {
        if (!cancelled) {
          setEffective(null);
          setError(err instanceof ApiError ? localizeApiError(err.message) : c.previewFailed);
        }
      })
      .finally(() => {
        if (!cancelled) setEffectiveLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [canPreviewEffective, previewSiteId, groups, c.previewFailed]);

  function resetSiteForm() {
    setSiteEditingId(null);
    setSiteCode("");
    setSiteName("");
    setSiteLocation("");
  }

  function loadSiteForEdit(site: AdminSite) {
    setSiteEditingId(site.id);
    setSiteCode(site.code);
    setSiteName(site.name);
    setSiteLocation(site.location ?? "");
  }

  function scrollToEffectivePreview(siteId: string) {
    setPreviewSiteId(siteId);
    previewRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  }

  async function handleSiteSave(e: FormEvent) {
    e.preventDefault();
    if (!canManageSites) return;
    setSiteSaving(true);
    setError(null);
    setSuccess(null);
    try {
      if (siteEditingId) {
        const target = allSites.find((s) => s.id === siteEditingId);
        await api.updateSite(siteEditingId, {
          code: target?.codeLocked ? undefined : siteCode,
          name: siteName,
          location: siteLocation || null,
        });
        setSuccess(c.siteUpdated);
      } else {
        await api.createSite({ code: siteCode, name: siteName, location: siteLocation || null });
        setSuccess(c.siteCreated);
        resetSiteForm();
      }
      await loadData();
    } catch (err) {
      setError(err instanceof ApiError ? localizeApiError(err.message) : c.saveFailed);
    } finally {
      setSiteSaving(false);
    }
  }

  async function deactivateSite(site: AdminSite) {
    setError(null);
    try {
      await api.updateSite(site.id, { active: false });
      setSuccess(`«${site.name}» ${c.siteDeactivated}`);
      await loadData();
    } catch (err) {
      setError(err instanceof ApiError ? localizeApiError(err.message) : c.actionFailed);
    }
  }

  function loadGroupForEdit(group: SiteGroup) {
    setEditingId(group.id);
    setName(group.name);
    setDescription(group.description);
    setMemberIds(group.members.map((m) => m.siteId));
    setConfirmMoves(false);
  }

  function toggleMember(siteId: string) {
    setMemberIds((prev) =>
      prev.includes(siteId) ? prev.filter((id) => id !== siteId) : [...prev, siteId],
    );
    setConfirmMoves(false);
  }

  async function applyPendingMoves(): Promise<void> {
    const removalsByGroup = new Map<string, Set<string>>();
    for (const move of pendingMoves) {
      const set = removalsByGroup.get(move.fromGroupId) ?? new Set<string>();
      set.add(move.siteId);
      removalsByGroup.set(move.fromGroupId, set);
    }
    for (const [groupId, removeIds] of removalsByGroup) {
      const group = groups.find((g) => g.id === groupId);
      if (!group) continue;
      const siteIds = group.members.map((m) => m.siteId).filter((id) => !removeIds.has(id));
      await api.updateSiteGroup(groupId, {
        name: group.name,
        description: group.description,
        siteIds,
      });
    }
  }

  async function handleSave(e: FormEvent) {
    e.preventDefault();
    if (!canManageGroups) return;
    if (pendingMoves.length > 0 && !confirmMoves) {
      setError(c.groupMoveConfirmRequired);
      return;
    }
    setSaving(true);
    setError(null);
    setSuccess(null);
    try {
      if (pendingMoves.length > 0) {
        await applyPendingMoves();
      }
      if (editingId) {
        await api.updateSiteGroup(editingId, { name, description, siteIds: memberIds });
        setSuccess(pendingMoves.length > 0 ? c.groupUpdatedWithMoves : c.groupUpdated);
      } else {
        await api.createSiteGroup({ name, description, siteIds: memberIds });
        setSuccess(pendingMoves.length > 0 ? c.groupCreatedWithMoves : c.groupCreated);
        resetForm();
      }
      setConfirmMoves(false);
      await loadData();
    } catch (err) {
      setError(err instanceof ApiError ? localizeApiError(err.message) : c.saveFailed);
    } finally {
      setSaving(false);
    }
  }

  async function handleResolveOverlaps(e: FormEvent) {
    e.preventDefault();
    if (overlaps.length === 0) return;
    setResolvingOverlaps(true);
    setError(null);
    setSuccess(null);
    try {
      const mappings = overlaps.map((row) => ({
        siteId: row.siteId,
        keepGroupId: overlapKeep[row.siteId] || row.groups[0]!.id,
      }));
      const result = await api.resolveSiteGroupOverlaps(mappings);
      setOverlaps(result.overlaps);
      setSuccess(`${c.overlapResolved} (${result.removed})`);
      await loadData();
    } catch (err) {
      setError(err instanceof ApiError ? localizeApiError(err.message) : c.overlapResolveFailed);
    } finally {
      setResolvingOverlaps(false);
    }
  }

  function renderSiteInspectFooter(site: SiteInventoryRow) {
    const tools: ReactNode[] = [];
    const primary: ReactNode[] = [];

    if (canManageSites) {
      tools.push(
        <button
          key="edit"
          type="button"
          className="btn btn--ghost btn--sm"
          onClick={() => {
            const admin = allSites.find((s) => s.id === site.id);
            if (admin) {
              setSelectedSiteId(site.id);
              loadSiteForEdit(admin);
            }
          }}
        >
          {c.edit}
        </button>,
      );
      if (site.active !== false) {
        primary.push(
          <button
            key="deactivate"
            type="button"
            className="btn btn--ghost btn--sm"
            onClick={() => {
              const admin = allSites.find((s) => s.id === site.id);
              if (admin) void deactivateSite(admin);
            }}
          >
            {c.deactivate}
          </button>,
        );
      }
    }
    if (canPreviewEffective) {
      tools.push(
        <button
          key="preview"
          type="button"
          className="btn btn--ghost btn--sm"
          onClick={() => {
            setSelectedSiteId(site.id);
            scrollToEffectivePreview(site.id);
          }}
        >
          {c.previewEffective}
        </button>,
      );
    }

    return (
      <div className="inspect-panel__footer-inner">
        {tools.length > 0 ? <div className="inspect-panel__footer-tools">{tools}</div> : null}
        {primary.length > 0 ? <div className="inspect-panel__footer-primary">{primary}</div> : null}
      </div>
    );
  }

  const showComposer = canManageSites || canManageGroups;
  const pageActions = showComposer ? (
    <>
      {canManageGroups ? (
        <a href="#site-group-composer" className="btn btn--ghost btn--sm">
          {c.createGroupCta}
        </a>
      ) : null}
      {canManageSites ? (
        <a href="#site-create-composer" className="btn btn--primary btn--sm">
          {c.createSiteCta}
        </a>
      ) : null}
    </>
  ) : undefined;

  return (
    <>
      <PageHeader title={cmsStrings.nav.sitesAndGroups} actions={pageActions} />

      {error && (
        <p className="banner banner--error" role="alert">
          {error}
        </p>
      )}
      {success && (
        <p className="banner banner--success" role="status">
          {success}
        </p>
      )}

      <div className="metric-strip" data-vt-inventory-metrics aria-label={ui.listTitle}>
        <MetricCard label={ui.filterAll} value={inventoryRows.length} loading={loading} />
        <MetricCard
          label={ui.metricUngrouped}
          value={ungroupedCount}
          loading={loading}
          tone={ungroupedCount > 0 ? "warning" : "neutral"}
        />
        <MetricCard
          label={ui.filterInactive}
          value={inactiveCount}
          loading={loading}
          sublabel={`${ui.filterActive}: ${activeCount}`}
        />
      </div>

      <div className="page-stack page-stack--inventory">
        {canResolveOverlaps && overlaps.length > 0 && (
          <section className="ops-alert ops-alert--overlap" aria-labelledby="sites-overlap-title">
            <div className="ops-alert__intro">
              <h2 id="sites-overlap-title" className="ops-alert__title">
                {ui.overlapAlertTitle}
              </h2>
              <p className="ops-alert__body">{ui.overlapAlertBody}</p>
            </div>
            <form className="ops-alert__form" onSubmit={(e) => void handleResolveOverlaps(e)}>
              <div className="ops-alert__fields">
                {overlaps.map((row) => (
                  <label key={row.siteId} className="ops-alert__field">
                    <span>
                      {c.overlapSiteLabel}{" "}
                      <bdi dir="ltr" className="ltr-isolate">
                        {row.siteCode}
                      </bdi>
                    </span>
                    <select
                      value={overlapKeep[row.siteId] ?? ""}
                      onChange={(e) =>
                        setOverlapKeep((prev) => ({ ...prev, [row.siteId]: e.target.value }))
                      }
                      required
                    >
                      {row.groups.map((g) => (
                        <option key={g.id} value={g.id}>
                          {g.name}
                          {!g.active ? ` (${c.overlapGroupInactiveSuffix})` : ""}
                        </option>
                      ))}
                    </select>
                  </label>
                ))}
              </div>
              <button type="submit" className="btn btn--primary btn--sm" disabled={resolvingOverlaps}>
                {resolvingOverlaps ? ui.overlapApplying : ui.overlapApply}
              </button>
            </form>
          </section>
        )}

        <div
          className={`content-ops-layout${selectedSite ? " content-ops-layout--inspect-open" : ""}`}
          data-vt-content-ops
          data-vt-inspect-open={selectedSite ? "true" : "false"}
        >
          <Card className="content-ops-layout__list inventory-surface" bodyClassName="card__body--flush">
            <SitesFilterBar
              query={siteQuery}
              onQueryChange={setSiteQuery}
              status={siteStatusFilter}
              onStatusChange={setSiteStatusFilter}
              sort={siteSort}
              onSortChange={setSiteSort}
              showStatusFilter={canManageSites}
            />
            {loading ? (
              <div className="inventory-skeleton" aria-busy="true" aria-label={cmsStrings.shell.loading}>
                <div className="inventory-skeleton__row" style={{ width: "92%" }} />
                <div className="inventory-skeleton__row" style={{ width: "78%" }} />
                <div className="inventory-skeleton__row" style={{ width: "85%" }} />
                <div className="inventory-skeleton__row" style={{ width: "70%" }} />
              </div>
            ) : inventoryRows.length === 0 ? (
              <OpsEmptyState title={ui.emptyTitle} />
            ) : visibleSites.length === 0 ? (
              <OpsEmptyState title={cmsStrings.inventory.noMatchTitle} />
            ) : (
              <SitesInventoryList
                rows={visibleSites}
                selectedId={selectedSiteId}
                onSelect={(id) => setSelectedSiteId((current) => (current === id ? null : id))}
                canManage={canManageSites}
                onEdit={(row) => {
                  const admin = allSites.find((s) => s.id === row.id);
                  if (admin) {
                    setSelectedSiteId(row.id);
                    loadSiteForEdit(admin);
                  }
                }}
                onDeactivate={(row) => {
                  const admin = allSites.find((s) => s.id === row.id);
                  if (admin) void deactivateSite(admin);
                }}
              />
            )}
          </Card>

          {selectedSite && (
            <InspectPanel
              title={ui.inspectTitle}
              className="content-inspect-panel"
              onClose={() => setSelectedSiteId(null)}
              closeLabel={c.close}
              footer={renderSiteInspectFooter(selectedSite)}
            >
              <SiteInspectSummary site={selectedSite} canReadResellers={canReadResellers} />
            </InspectPanel>
          )}
        </div>

        {showComposer ? (
          <ComposerWorkspace id="site-composer" className="sites-composer-workspace">
            <div
              className={`sites-composer-split${canManageSites && canManageGroups ? "" : " sites-composer-split--solo"}`}
            >
              {canManageGroups ? (
                <ComposerPanel className="sites-composer-split__panel">
                  <ComposerHeader
                    kind={cmsStrings.composerChrome.kindSiteGroup}
                    title={editingId ? c.groupCardTitleEdit : c.groupCardTitleCreate}
                    statusLabel={
                      editingId
                        ? cmsStrings.composerChrome.statusEditDraft
                        : cmsStrings.composerChrome.statusCreate
                    }
                    statusTone={editingId ? "warning" : "info"}
                  />
                  <form
                    id="site-group-composer"
                    className="content-form content-form--in-card composer-form composer-form--group"
                    onSubmit={(e) => void handleSave(e)}
                  >
                    <ComposerSection
                      id="site-group-section-basic"
                      title={cmsStrings.composerChrome.sectionBasic}
                      bare
                    >
                      <label>
                        <span className="composer-label">
                          {c.groupNameLabel}
                          <bdi dir="ltr" className="composer-label__count ltr-isolate latin-digits">
                            {name.length}/{GROUP_NAME_MAX}
                          </bdi>
                        </span>
                        <input
                          value={name}
                          onChange={(e) => setName(e.target.value.slice(0, GROUP_NAME_MAX))}
                          required
                          maxLength={GROUP_NAME_MAX}
                        />
                      </label>
                      <label>
                        <span className="composer-label">
                          {c.groupDescriptionLabel} — {c.groupDescriptionOptional}
                          <bdi dir="ltr" className="composer-label__count ltr-isolate latin-digits">
                            {description.length}/{GROUP_DESC_MAX}
                          </bdi>
                        </span>
                        <textarea
                          value={description}
                          onChange={(e) => setDescription(e.target.value.slice(0, GROUP_DESC_MAX))}
                          maxLength={GROUP_DESC_MAX}
                          rows={2}
                        />
                      </label>
                    </ComposerSection>
                    <ComposerSection id="site-group-section-members" title={c.groupMembersLegend} bare>
                      {sites.length > 0 ? (
                        <div className="composer-member-checklist">
                          {sites.map((site) => {
                            const checked = memberIds.includes(site.id);
                            return (
                              <label key={site.id} className="checkbox-label">
                                <input
                                  type="checkbox"
                                  checked={checked}
                                  onChange={() => toggleMember(site.id)}
                                />
                                <span>
                                  {site.name}{" "}
                                  <bdi dir="ltr" className="ltr-isolate">
                                    ({site.code})
                                  </bdi>
                                </span>
                              </label>
                            );
                          })}
                        </div>
                      ) : null}
                      {pendingMoves.length > 0 ? (
                        <div className="banner banner--warning">
                          <p>
                            {pendingMoves.length} {c.groupMoveWarning}
                          </p>
                          <ul className="effective-block__candidates">
                            {pendingMoves.map((m) => (
                              <li key={m.siteId}>
                                {m.siteName}{" "}
                                <bdi dir="ltr" className="ltr-isolate">
                                  ({m.siteCode})
                                </bdi>{" "}
                                {c.groupMemberMoveFrom} «{m.fromGroupName}»
                              </li>
                            ))}
                          </ul>
                          <label className="checkbox-label">
                            <input
                              type="checkbox"
                              checked={confirmMoves}
                              onChange={(e) => setConfirmMoves(e.target.checked)}
                            />
                            <span>{c.groupMoveConfirm}</span>
                          </label>
                        </div>
                      ) : null}
                    </ComposerSection>
                    {groups.length > 0 ? (
                      <ComposerSection id="site-group-section-list" title={ui.groupsListTitle} bare>
                        <SiteGroupsRoster
                          rows={groupRows}
                          canEdit={canManageGroups}
                          onEdit={(id) => {
                            const group = groups.find((g) => g.id === id);
                            if (group) loadGroupForEdit(group);
                          }}
                          editLabel={c.edit}
                          membersUnit={ui.membersUnit}
                        />
                      </ComposerSection>
                    ) : null}
                    <div className="form-actions form-actions--end">
                      <button type="submit" className="btn btn--primary" disabled={saving}>
                        {saving ? c.saving : editingId ? c.updateGroup : c.saveGroup}
                      </button>
                      {editingId ? (
                        <button type="button" className="btn btn--ghost" onClick={resetForm}>
                          {c.cancel}
                        </button>
                      ) : null}
                    </div>
                  </form>
                </ComposerPanel>
              ) : null}

              {canManageSites ? (
                <ComposerPanel className="sites-composer-split__panel">
                  <ComposerHeader
                    kind={cmsStrings.composerChrome.kindSite}
                    title={siteEditingId ? c.siteCardTitleEdit : c.siteCardTitleCreate}
                    statusLabel={
                      siteEditingId
                        ? cmsStrings.composerChrome.statusEditDraft
                        : cmsStrings.composerChrome.statusCreate
                    }
                    statusTone={siteEditingId ? "warning" : "info"}
                  />
                  <form
                    id="site-create-composer"
                    className="content-form content-form--in-card composer-form composer-form--site"
                    onSubmit={(e) => void handleSiteSave(e)}
                  >
                    <ComposerSection
                      id="site-section-basic"
                      title={cmsStrings.composerChrome.sectionBasic}
                      bare
                    >
                      <label>
                        <span>{c.siteCodeLabel}</span>
                        <input
                          value={siteCode}
                          onChange={(e) => setSiteCode(e.target.value)}
                          required
                          disabled={Boolean(
                            siteEditingId && allSites.find((s) => s.id === siteEditingId)?.codeLocked,
                          )}
                          dir="ltr"
                          className="ltr-isolate"
                        />
                      </label>
                      <label>
                        <span>{c.siteNameLabel}</span>
                        <input
                          value={siteName}
                          onChange={(e) => setSiteName(e.target.value)}
                          required
                        />
                      </label>
                      <label>
                        <span>
                          {c.siteLocationLabel} — {c.siteLocationOptional}
                        </span>
                        <input
                          value={siteLocation}
                          onChange={(e) => setSiteLocation(e.target.value)}
                        />
                      </label>
                    </ComposerSection>
                    <div className="form-actions form-actions--end">
                      <button type="submit" className="btn btn--primary" disabled={siteSaving}>
                        {siteSaving ? c.saving : siteEditingId ? c.updateSite : c.saveSite}
                      </button>
                      {siteEditingId ? (
                        <button type="button" className="btn btn--ghost" onClick={resetSiteForm}>
                          {c.cancel}
                        </button>
                      ) : null}
                    </div>
                  </form>
                </ComposerPanel>
              ) : null}
            </div>

            {canPreviewEffective ? (
              <Card
                title={ui.previewTitle}
                className="preview-panel"
                bodyClassName="card__body--flush"
              >
                <div ref={previewRef} id="effective-preview">
                  <div className="content-form content-form--in-card composer-form composer-form--preview">
                    <label>
                      <span>{ui.previewSiteLabel}</span>
                      <select
                        value={previewSiteId}
                        onChange={(e) => setPreviewSiteId(e.target.value)}
                        dir="ltr"
                        className="ltr-isolate"
                      >
                        {sites.map((site) => (
                          <option key={site.id} value={site.id}>
                            {site.code} — {site.name}
                          </option>
                        ))}
                      </select>
                    </label>
                  </div>
                  <EffectiveContentPanel effective={effective} loading={effectiveLoading} />
                </div>
              </Card>
            ) : null}
          </ComposerWorkspace>
        ) : canPreviewEffective ? (
          <div ref={previewRef} id="effective-preview">
            <Card
              title={ui.previewTitle}
              className="preview-panel"
              bodyClassName="card__body--flush"
            >
              <div className="content-form content-form--in-card composer-form composer-form--preview">
                <label>
                  <span>{ui.previewSiteLabel}</span>
                  <select
                    value={previewSiteId}
                    onChange={(e) => setPreviewSiteId(e.target.value)}
                    dir="ltr"
                    className="ltr-isolate"
                  >
                    {sites.map((site) => (
                      <option key={site.id} value={site.id}>
                        {site.code} — {site.name}
                      </option>
                    ))}
                  </select>
                </label>
              </div>
              <EffectiveContentPanel effective={effective} loading={effectiveLoading} />
            </Card>
          </div>
        ) : null}
      </div>
    </>
  );
}
